/**
 * Centralized chat theme — used by all chat interfaces.
 * Single source of truth for colors, spacing, and typography.
 *
 * Import: import { chatTheme } from '../../theme/chat';
 * Usage:  className={chatTheme.bg}
 */

export const chatTheme = {
  // ── Page / Layout ──
  pageBg: 'bg-white',
  panelWidth: 'w-96',
  headerHeight: 'h-12',

  // ── Base Colors ──
  bg: 'bg-white',
  border: 'border-zinc-200',
  text: {
    primary: 'text-zinc-900',
    secondary: 'text-zinc-700',
    muted: 'text-zinc-500',
    faint: 'text-zinc-400',
    accent: 'text-accent',
  },

  // ── User Message ──
  userBubble: 'bg-zinc-100 rounded-xl px-4 py-2.5 text-zinc-800 text-sm',
  userIcon: 'bg-zinc-100 text-zinc-500',

  // ── Assistant Message ──
  assistantText: 'text-zinc-800 text-sm leading-relaxed',
  botIcon: 'bg-accent/10 text-accent',

  // ── Tool Calls ──
  toolCall: {
    bg: 'bg-zinc-100',
    text: 'text-zinc-500',
    running: 'text-accent',
    done: 'text-emerald-500/60',
    // Card style (ToolCallCard)
    card: {
      bg: 'bg-stone-50',
      border: 'border-stone-200/60',
      hoverBg: 'hover:bg-stone-100/60',
      chevron: 'text-stone-400',
      dot: {
        running: 'bg-accent',
        done: 'bg-emerald-500',
        error: 'bg-red-400',
      },
      name: 'text-stone-700',
      badge: {
        running: 'text-accent bg-accent/10',
        done: 'text-emerald-600 bg-emerald-50',
        error: 'text-red-600 bg-red-50',
      },
      result: {
        bg: 'bg-white',
        border: 'border-t border-stone-200/40',
        text: 'text-stone-600',
      },
    },
  },

  // ── Input Area ──
  input: {
    bg: 'bg-zinc-50',
    border: 'border-zinc-200',
    text: 'text-zinc-800',
    placeholder: 'placeholder-zinc-400',
    iconColor: 'text-zinc-400',
    iconHover: 'hover:text-accent',
    wrapper: 'bg-zinc-50 border border-zinc-200 rounded-2xl px-4 py-3',
  },

  // ── Company Pills ──
  pill: {
    bg: 'bg-white',
    border: 'border-zinc-200',
    text: 'text-zinc-600',
    remove: 'text-zinc-400 hover:text-zinc-700',
  },

  // ── Dropdown ──
  dropdown: {
    bg: 'bg-white',
    border: 'border-zinc-200',
    shadow: 'shadow-xl',
    headerText: 'text-zinc-500',
    headerBorder: 'border-zinc-100',
    itemText: 'text-zinc-700',
    itemHover: 'hover:bg-zinc-50',
    selectedItemText: 'text-zinc-800',
    searchText: 'text-zinc-700',
    searchPlaceholder: 'placeholder-zinc-400',
    muted: 'text-zinc-400',
    pillRemove: 'text-zinc-400 hover:text-zinc-700',
    addBtn: 'text-zinc-400 hover:text-zinc-700 border-zinc-300 hover:border-zinc-400',
    triggerBtn: 'text-zinc-500 hover:text-zinc-800',
    triggerIcon: 'text-zinc-400 hover:text-accent',
    syncDone: 'bg-emerald-50 text-emerald-700',
    syncFail: 'bg-red-50 text-red-700',
    syncRun: 'bg-blue-50 text-blue-700',
  },

  // ── Prose / Markdown ──
  prose: `prose prose-sm max-w-none
    prose-headings:text-zinc-800 prose-headings:font-semibold prose-headings:mt-4 prose-headings:mb-2
    prose-p:text-zinc-700 prose-p:my-1.5
    prose-strong:text-zinc-900
    prose-li:text-zinc-700 prose-li:my-0.5
    prose-table:text-sm prose-th:text-zinc-700 prose-th:px-3 prose-th:py-1.5 prose-th:border-zinc-200 prose-th:bg-zinc-50
    prose-td:text-zinc-700 prose-td:px-3 prose-td:py-1.5 prose-td:border-zinc-200
    prose-hr:border-zinc-200 prose-hr:my-3
    prose-code:text-accent prose-code:bg-zinc-100 prose-code:px-1 prose-code:rounded
    prose-a:text-accent`,

  proseCompact: `prose prose-xs max-w-none
    prose-headings:text-zinc-800 prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-1
    prose-p:text-zinc-700 prose-p:my-1
    prose-strong:text-zinc-900
    prose-li:text-zinc-700 prose-li:my-0.5
    prose-table:text-xs prose-th:text-zinc-700 prose-th:px-2 prose-th:py-1 prose-th:border-zinc-200 prose-th:bg-zinc-50
    prose-td:text-zinc-700 prose-td:px-2 prose-td:py-1 prose-td:border-zinc-200
    prose-hr:border-zinc-200 prose-hr:my-2
    prose-code:text-accent prose-code:bg-zinc-100 prose-code:px-1 prose-code:rounded
    prose-a:text-accent`,

  // ── Empty / Greeting State ──
  emptyIcon: 'text-accent/30',
  emptyText: 'text-zinc-400',
  greeting: {
    text: 'text-zinc-800',
    accent: 'text-accent',
  },

  // ── Quick Actions ──
  quickAction: {
    text: 'text-zinc-500',
    border: 'border-zinc-200',
    hover: 'hover:text-zinc-800 hover:border-zinc-400 hover:bg-zinc-50',
  },

  // ── Copy Button ──
  copyBtn: {
    base: 'text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100',
    success: 'text-emerald-500',
  },

  // ── Thread List (Sidebar) ──
  sidebar: {
    bg: 'bg-white',
    border: 'border-r border-zinc-200',
    newBtn: 'text-zinc-700 bg-zinc-50 hover:bg-zinc-100 border border-zinc-200',
    collapseBtn: 'text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100',
    groupLabel: 'text-zinc-400',
    threadActive: 'bg-zinc-100 text-zinc-900',
    threadInactive: 'text-zinc-500 hover:bg-zinc-50 hover:text-zinc-700',
    emptyText: 'text-zinc-400',
    icon: 'text-zinc-400',
  },

  // ── Loading / Spinner ──
  spinner: 'text-accent',
  thinkingText: 'text-zinc-400',

  // ── Usage Bar ──
  usageText: 'text-zinc-400',

  // ── Bottom Bar (input container area) ──
  bottomBar: {
    border: 'border-t border-zinc-200',
    bg: 'bg-white',
  },

  // ── Image attachment border ──
  imageBorder: 'border-zinc-200',

  // ── Model selector ──
  modelSelector: 'text-zinc-400 bg-transparent text-sm focus:outline-none cursor-pointer',

  // ── Send button ──
  sendBtn: {
    active: 'text-zinc-500 hover:text-accent',
    disabled: 'text-zinc-300',
  },

  // ── Chart ──
  // Keep in sync with template-agent/app/theme/chart.py
  chart: {
    // Muted financial reporting palette — keep in sync with template-agent/app/theme/chart.py
    colors: ['#5B8DB8', '#66C2A5', '#E8956A', '#8DA0CB', '#B5AA68', '#E78AC3', '#A2B969', '#D4A76A', '#9C9C9C', '#7FCDBB'],
    textPrimary: '#111827',   // gray-900 — titles
    textSecondary: '#374151', // gray-700 — body
    textMuted: '#6b7280',     // gray-500 — axis labels, legends
    axisLine: '#d1d5db',      // gray-300
    gridLine: '#f3f4f6',      // gray-100
    containerBg: 'bg-zinc-50/50',
    containerBorder: 'border-zinc-200',
  },

  // ── Agent Page Layout ──
  page: {
    bg: 'bg-white',
    sidebarWidth: 'w-[340px]',
    sidebarBg: 'bg-white',
    sidebarBorder: 'border-r border-zinc-200',
    chatWidth: 'w-96',
    chatBorder: 'border-l border-zinc-200',
    headerHeight: 'h-12',
    headerBorder: 'border-b border-zinc-200',
    headerText: 'text-sm font-semibold text-zinc-800',
    headerIcon: 'text-zinc-500',
    badge: 'text-[10px] text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded',
  },

  // ── Item Sidebar ──
  itemSidebar: {
    sectionLabel: 'text-[10px] font-semibold text-zinc-400 uppercase tracking-wider',
    templateCard: {
      base: 'w-full text-left px-3 py-2.5 rounded-lg border transition-colors hover:brightness-105',
      name: 'text-xs font-semibold text-zinc-800',
      description: 'text-[10px] text-zinc-500 mt-0.5 ml-6',
    },
    item: {
      base: 'flex items-center justify-between group px-2.5 py-2 rounded-md cursor-pointer transition-colors border',
      active: 'bg-accent/5 border-accent/20',
      inactive: 'hover:bg-zinc-50 border-transparent',
      name: 'text-xs text-zinc-800 truncate',
      meta: 'text-[10px] text-zinc-400 truncate',
      deleteBtn: 'opacity-0 group-hover:opacity-100 p-1 text-zinc-400 hover:text-red-500 transition-all',
    },
    empty: 'text-[11px] text-zinc-400 italic',
    divider: 'border-t border-zinc-200',
  },

  // ── Workspace ──
  workspace: {
    titleBar: {
      height: 'h-11',
      bg: 'bg-white',
      border: 'border-b border-zinc-200',
      title: 'text-sm font-semibold text-zinc-900',
      description: 'text-xs text-zinc-400',
      actionBtn: 'p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-50 rounded-md transition-colors',
      runBtn: 'flex items-center gap-1.5 px-3 py-1.5 text-xs bg-accent text-white rounded-md hover:bg-accent/90 transition-colors disabled:opacity-50 font-medium',
      separator: 'w-px h-5 bg-zinc-200',
    },
    query: {
      bg: 'bg-zinc-50/80',
      border: 'border-b border-zinc-200',
      headerBg: 'bg-zinc-100/50',
      keyword: 'text-blue-600 font-medium',
      field: 'text-zinc-700',
      comment: 'text-zinc-400 italic',
      value: 'text-emerald-600',
      operator: 'text-purple-600',
    },
    table: {
      toolbarBg: 'bg-white',
      toolbarBorder: 'border-b border-zinc-200',
      toolbarText: 'text-xs font-medium text-zinc-600',
      toolbarCount: 'text-zinc-400',
      headerBg: 'bg-zinc-50',
      headerBorder: 'border-b border-zinc-200',
      headerCell: 'text-[11px] font-semibold text-zinc-500 uppercase tracking-wide',
      cellText: 'text-[13px] text-zinc-700',
      hoverRow: 'hover:bg-zinc-50/50',
      totalColBg: 'bg-zinc-50/50',
      searchInput: 'pl-7 pr-3 py-1.5 text-xs border border-zinc-200 rounded-md focus:outline-none focus:ring-1 focus:ring-accent/50 focus:border-accent bg-zinc-50 w-48',
      exportBtn: 'flex items-center gap-1.5 px-2.5 py-1 text-xs border border-emerald-200 rounded-md text-emerald-700 hover:bg-emerald-50 transition-colors disabled:opacity-50',
    },
    row: {
      sectionHeader: 'bg-zinc-100 text-zinc-800 text-[11px] font-bold uppercase tracking-wider',
      account: 'text-zinc-700 border-b border-zinc-100 hover:bg-zinc-50/50',
      subtotal: 'text-zinc-900 bg-zinc-50 font-semibold border-t border-zinc-200',
      kpi: 'bg-emerald-50/50 text-emerald-700 font-semibold border-t-2 border-emerald-200',
      separator: 'border-zinc-200',
      negative: 'text-red-600',
    },
    emptyState: 'text-sm text-zinc-400',
  },

  // ── Form ──
  form: {
    bg: 'bg-white',
    border: 'border-b border-zinc-200',
    label: 'text-[10px] font-medium text-zinc-500 uppercase tracking-wide',
    input: 'px-2 py-1.5 text-xs bg-zinc-50 border border-zinc-300 rounded text-zinc-800 focus:outline-none focus:border-accent',
    select: 'px-2 py-1.5 text-xs bg-zinc-50 border border-zinc-300 rounded text-zinc-800 focus:outline-none focus:border-accent',
    checkbox: 'rounded border-zinc-300 bg-white text-accent focus:ring-accent focus:ring-offset-0 h-3 w-3',
    checkboxLabel: 'text-xs text-zinc-700 cursor-pointer',
    primaryBtn: 'flex items-center gap-1.5 px-4 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 font-medium',
    secondaryBtn: 'flex items-center gap-1.5 px-3 py-1.5 text-xs border border-zinc-300 text-zinc-700 rounded hover:bg-zinc-100 transition-colors disabled:opacity-40 font-medium',
  },

  // ── Reconciliation ──
  reconciliation: {
    pass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    fail: 'bg-red-50 text-red-700 border-red-200',
    error: 'bg-amber-50 text-amber-700 border-amber-200',
    tierHeader: 'text-xs font-medium text-zinc-700',
    comparison: {
      headerBg: 'bg-zinc-50',
      headerText: 'text-[10px] text-zinc-500 uppercase tracking-wide',
      cellText: 'text-xs text-zinc-700 tabular-nums',
      matchRow: 'bg-emerald-50/30',
      failRow: 'bg-red-50/30',
    },
  },

  // ── Template colors ──
  templates: {
    pnl: { bg: 'bg-emerald-50 border-emerald-200', color: 'text-emerald-600' },
    balance_sheet: { bg: 'bg-blue-50 border-blue-200', color: 'text-blue-600' },
    cash_flow: { bg: 'bg-purple-50 border-purple-200', color: 'text-purple-600' },
  },

  // ── Error ──
  error: {
    banner: 'text-red-600 bg-red-50 border border-red-200 rounded',
  },
};
